import { useState, useEffect, useCallback, createContext, useContext, useMemo } from 'react';
import type { CartItem, Product, ProductVariant } from '../types/product';

const CART_STORAGE_KEY = 'cart';

interface CartContextType {
  cartItems: CartItem[];
  addToCart: (product: Product, quantity?: number, selectedVariant?: ProductVariant) => void;
  updateQuantity: (productId: string, variantId: string, quantity: number) => void;
  incrementQuantity: (productId: string, variantId: string) => void;
  decrementQuantity: (productId: string, variantId: string) => void;
  removeItem: (productId: string, variantId: string) => void;
  clearCart: () => void;
  clearInvalidItems: (availableProducts: Product[]) => void;
  subtotal: number;
  itemCount: number;
  isLoaded: boolean;
}

const CartContext = createContext<CartContextType | null>(null);

function getCartFromStorage(): CartItem[] {
  if (typeof window === 'undefined') return [];
  
  try {
    const stored = localStorage.getItem(CART_STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('Failed to load cart from storage:', error);
    return [];
  }
}

function saveCartToStorage(cart: CartItem[]): void {
  if (typeof window === 'undefined') return;
  
  try {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cart));
  } catch (error) {
    console.error('Failed to save cart to storage:', error);
  }
}

function createDefaultVariant(product: Product): ProductVariant {
  return {
    id: `${product.id}-standard`,
    name: 'Standard',
    price: product.price,
    image: product.image
  };
}

function getSelectedVariant(product: Product, selectedVariant?: ProductVariant): ProductVariant {
  if (selectedVariant) {
    return selectedVariant;
  }
  
  if (product.variants && product.variants.length > 0) {
    return product.variants[0];
  }
  
  return createDefaultVariant(product);
}

// Global cart opener for cart drawer
let globalCartOpener: (() => void) | null = null;

export function setCartOpener(opener: () => void) {
  globalCartOpener = opener;
}

export function openCart() {
  if (globalCartOpener) {
    globalCartOpener();
  }
}

/**
 * Validates if a variant ID is a valid Shopify GID format
 * Accepts: "gid://shopify/ProductVariant/12345" or numeric IDs
 */
function isValidShopifyVariantId(variantId: string | undefined): boolean {
  if (!variantId) return false;
  
  // Check if it's a Shopify GID format
  if (variantId.includes('gid://shopify/ProductVariant/')) return true;
  
  // Check if it's a numeric ID
  if (/^\d+$/.test(variantId)) return true;
  
  // Check if it's a mock variant ID (product-id-standard format)
  if (variantId.includes('-standard')) return true;
  
  return false;
}

/**
 * Validates cart items against available products catalog
 * Removes items that:
 * - Reference products that no longer exist
 * - Have invalid variant IDs
 * - Reference variants that no longer exist in the product
 */
export function validateCartItems(cartItems: CartItem[], availableProducts: Product[]): CartItem[] {
  if (!availableProducts || availableProducts.length === 0) {
    // Keep items if products haven't loaded yet
    return cartItems;
  }

  const validatedItems: CartItem[] = [];
  let removedCount = 0;

  for (const item of cartItems) {
    // Check if product exists
    const product = availableProducts.find(p => p.id === item.id);
    
    if (!product) {
      console.warn(`[Cart Validation] Removing item "${item.name}" - product no longer exists`);
      removedCount++;
      continue;
    }

    // Check if variant ID is valid format
    if (!isValidShopifyVariantId(item.selectedVariantId)) {
      console.warn(`[Cart Validation] Removing item "${item.name}" - invalid variant ID format: ${item.selectedVariantId}`);
      removedCount++;
      continue;
    }

    // Check if variant exists in product
    if (product.variants && product.variants.length > 0) {
      const variantExists = product.variants.some(v => v.id === item.selectedVariantId);
      
      if (!variantExists) {
        console.warn(`[Cart Validation] Removing item "${item.name}" - variant no longer exists in product`);
        removedCount++;
        continue;
      }
    }

    // Item is valid
    validatedItems.push(item);
  }

  if (removedCount > 0) {
    console.log(`[Cart Validation] Removed ${removedCount} invalid item(s) from cart`);
  }

  return validatedItems;
}

// Global cart state - single source of truth
let globalCartState: CartItem[] = [];
let globalCartListeners: Set<(items: CartItem[]) => void> = new Set();
let updateInProgress = false;
let pendingUpdate: (() => void) | null = null;

/**
 * Thread-safe cart update with deterministic state management
 * Prevents race conditions by queuing updates
 */
function updateGlobalCart(newItems: CartItem[]) {
  // If update in progress, queue this update
  if (updateInProgress) {
    pendingUpdate = () => updateGlobalCart(newItems);
    return;
  }

  updateInProgress = true;

  try {
    // Update global state
    globalCartState = [...newItems];
    
    // Persist to storage
    saveCartToStorage(globalCartState);
    
    // Notify all listeners with immutable copy
    globalCartListeners.forEach(listener => {
      try {
        listener([...globalCartState]);
      } catch (error) {
        console.error('Cart listener error:', error);
      }
    });
  } finally {
    updateInProgress = false;

    // Process pending update if any
    if (pendingUpdate) {
      const nextUpdate = pendingUpdate;
      pendingUpdate = null;
      nextUpdate();
    }
  }
}

// Initialize global cart from localStorage
if (typeof window !== 'undefined') {
  globalCartState = getCartFromStorage();
}

export function useCart() {
  const [cartItems, setCartItems] = useState<CartItem[]>([...globalCartState]);
  const [isLoaded, setIsLoaded] = useState(false);

  // Subscribe to global cart changes
  useEffect(() => {
    const listener = (newItems: CartItem[]) => {
      setCartItems([...newItems]);
    };
    
    globalCartListeners.add(listener);
    
    // Initialize with current global state
    setCartItems([...globalCartState]);
    setIsLoaded(true);
    
    return () => {
      globalCartListeners.delete(listener);
    };
  }, []);

  const addToCart = useCallback((
    product: Product, 
    quantity: number = 1, 
    selectedVariant?: ProductVariant
  ) => {
    if (!product || quantity <= 0) return;

    try {
      const variant = getSelectedVariant(product, selectedVariant);
      
      // Validate variant ID before adding
      if (!isValidShopifyVariantId(variant.id)) {
        console.error('[Cart] Cannot add item with invalid variant ID:', variant.id);
        return;
      }
      
      const existingItemIndex = globalCartState.findIndex(item => 
        item.id === product.id && item.selectedVariantId === variant.id
      );

      let newCart: CartItem[];

      if (existingItemIndex >= 0) {
        // Update existing item quantity
        newCart = globalCartState.map((item, index) =>
          index === existingItemIndex
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      } else {
        // Add new item
        const cartItem: CartItem = {
          ...product,
          quantity,
          selectedVariantId: variant.id,
          selectedVariantName: variant.name,
          selectedVariantPrice: variant.price,
          selectedVariantImage: variant.image
        };
        newCart = [...globalCartState, cartItem];
      }
      
      updateGlobalCart(newCart);
    } catch (error) {
      console.error('Failed to add item to cart:', error);
    }
  }, []);

  const updateQuantity = useCallback((productId: string, variantId: string, quantity: number) => {
    if (!productId || !variantId || quantity < 0) return;

    try {
      let newCart: CartItem[];
      
      if (quantity === 0) {
        // Remove item if quantity is 0
        newCart = globalCartState.filter(item => 
          !(item.id === productId && item.selectedVariantId === variantId)
        );
      } else {
        // Update quantity
        newCart = globalCartState.map(item =>
          item.id === productId && item.selectedVariantId === variantId 
            ? { ...item, quantity } 
            : item
        );
      }
      
      updateGlobalCart(newCart);
    } catch (error) {
      console.error('Failed to update cart quantity:', error);
    }
  }, []);

  const incrementQuantity = useCallback((productId: string, variantId: string) => {
    if (!productId || !variantId) return;

    try {
      const item = globalCartState.find(item => 
        item.id === productId && item.selectedVariantId === variantId
      );
      
      if (!item) {
        console.warn('Item not found in cart for increment:', { productId, variantId });
        return;
      }

      const newCart = globalCartState.map(cartItem =>
        cartItem.id === productId && cartItem.selectedVariantId === variantId
          ? { ...cartItem, quantity: cartItem.quantity + 1 }
          : cartItem
      );
      
      updateGlobalCart(newCart);
    } catch (error) {
      console.error('Failed to increment cart quantity:', error);
    }
  }, []);

  const decrementQuantity = useCallback((productId: string, variantId: string) => {
    if (!productId || !variantId) return;

    try {
      const item = globalCartState.find(item => 
        item.id === productId && item.selectedVariantId === variantId
      );
      
      if (!item) {
        console.warn('Item not found in cart for decrement:', { productId, variantId });
        return;
      }

      let newCart: CartItem[];
      
      if (item.quantity <= 1) {
        // Remove item if quantity would be 0
        newCart = globalCartState.filter(cartItem => 
          !(cartItem.id === productId && cartItem.selectedVariantId === variantId)
        );
      } else {
        // Decrement quantity
        newCart = globalCartState.map(cartItem =>
          cartItem.id === productId && cartItem.selectedVariantId === variantId
            ? { ...cartItem, quantity: cartItem.quantity - 1 }
            : cartItem
        );
      }
      
      updateGlobalCart(newCart);
    } catch (error) {
      console.error('Failed to decrement cart quantity:', error);
    }
  }, []);

  const removeItem = useCallback((productId: string, variantId: string) => {
    if (!productId || !variantId) return;

    try {
      const newCart = globalCartState.filter(item => 
        !(item.id === productId && item.selectedVariantId === variantId)
      );
      
      updateGlobalCart(newCart);
    } catch (error) {
      console.error('Failed to remove item from cart:', error);
    }
  }, []);

  const clearCart = useCallback(() => {
    try {
      updateGlobalCart([]);
    } catch (error) {
      console.error('Failed to clear cart:', error);
    }
  }, []);

  const clearInvalidItems = useCallback((availableProducts: Product[]) => {
    const validatedCart = validateCartItems(globalCartState, availableProducts);
    if (validatedCart.length !== globalCartState.length) {
      console.log(`[Cart] Cleared ${globalCartState.length - validatedCart.length} invalid item(s)`);
      updateGlobalCart(validatedCart);
    }
  }, []);

  // Calculate subtotal - use cartItems state for reactive updates
  const subtotal = cartItems.reduce(
    (sum, item) => sum + (item.selectedVariantPrice || item.price) * item.quantity,
    0
  );

  // Calculate total item count - sum of all quantities
  const itemCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);

  return {
    cartItems,
    addToCart,
    updateQuantity,
    incrementQuantity,
    decrementQuantity,
    removeItem,
    clearCart,
    clearInvalidItems,
    subtotal,
    itemCount,
    isLoaded,
  };
}
